import { Component, OnInit, OnDestroy } from '@angular/core';
import { forkJoin, Observable, empty } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { ApplicationState } from '../../../store/application-store';
import * as widgetConfigActions from '../../../widget-configs/actions/widget-configs';
import * as widgetConfigurationSelectors from '../../../widget-configs/selectors/widget-configs';
import * as lodash from 'lodash';
import { ToastrService } from 'ngx-toastr';
import * as ProfileTypes from '../../../profiles/models/profile-types';
import { map, tap, take, takeWhile, catchError } from 'rxjs';
import { RouteContextService } from '../../../context/services/route-context.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MojoWellKnownNames } from '../../../common/constants/mojo-well-known-names';
import { ApplicationModeService } from '../../../application-mode/services/application-mode.service';
import { ProductService } from '../../../common/services/product.service';

import { DefaultWidgetAdminHandlerProvider, WidgetAdminHandler } from '@feature/widget-admin/old_index';
import { wrapIntoObservable } from '@common/index';
import { WidgetConfiguration } from '@feature/widget-configs';
import { FormDefinition } from '@feature/_app_modules/dynamic-forms/dynamic-form';
import { WidgetColumnsHttpService } from '@feature/widget-columns/services/widget-columns-http.service';

@Component({
  selector: 'mojo-widget-admin-page',
  templateUrl: './widget-admin-page.component.html',
})
export class WidgetAdminPageComponent implements OnInit, OnDestroy {

  isAlive: boolean = true;
  pageTitle$: Observable<string>;
  dialogTitle = 'Widget Properties';
  displayDialog: boolean = false;
  displayDeleteDialog: boolean = false;
  formDefinition: Observable<FormDefinition>;
  widgetConfigurations$: Observable<WidgetConfiguration[]>;
  selectedWidgetConfiguration: WidgetConfiguration;
  loading$: Observable<boolean>;
  product: string;
  useMojoAdminStyling: boolean;

  constructor(
    protected store: Store<ApplicationState>,
    private readonly routeContextService: RouteContextService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly messageService: ToastrService,
    appMode: ApplicationModeService,
    private readonly productService: ProductService,
    private readonly widgetAdminHandlerProvider: DefaultWidgetAdminHandlerProvider,
    private readonly widgetColumnsHttpService: WidgetColumnsHttpService
  ) {

    appMode.isInMyDashboardMode().pipe(takeWhile(() => this.isAlive)).subscribe((mode) => this.useMojoAdminStyling = mode);
    this.setPageTitle();

    this.product = this.routeContextService.getProductFromRouteData(this.activatedRoute);
    this.store.dispatch(new widgetConfigActions.LoadAll(this.product));
    this.widgetConfigurations$ = this.store.pipe(select(widgetConfigurationSelectors.selectAll));
    this.loading$ = this.store.pipe(select(widgetConfigurationSelectors.selectLoading()));

    const all = widgetAdminHandlerProvider.all().map(kvp => {
      const [typeId, handler] = kvp;
      return this.handleInitialisation(handler);
    });

    forkJoin(all).subscribe(r => {
      this.store.dispatch(new widgetConfigActions.LoadAll(this.product));
      console.log(`All WidgetAdminConfigurators Initialized: ${r}`);
    });

  }

  private handleInitialisation(handler: WidgetAdminHandler): Observable<boolean> {
    return wrapIntoObservable(handler.initialise());
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.isAlive = false;
  }

  displayDeleteButton() {
    if (this.selectedWidgetConfiguration) {
      return this.selectedWidgetConfiguration.type === ProfileTypes.Custom;
    }
    return false;
  }

  displayResetAllUsersButton() {
    const unitBvAWidgetNames = [ "MojoBudgetVsActualWidget", "MojoBudgetVsActualUnitWidget", "MftBudgetVsActualUnitWidget" ];

    if (this.selectedWidgetConfiguration)
      return unitBvAWidgetNames.includes(this.selectedWidgetConfiguration.widgetTypeId);

    return false;
  }

  configSelected(selectedWidgetConfig: WidgetConfiguration) {
    this.selectedWidgetConfiguration = selectedWidgetConfig;

    this.formDefinition = wrapIntoObservable(this.widgetAdminHandlerProvider.get(selectedWidgetConfig.widgetTypeId).getAdminForm(lodash.cloneDeep(selectedWidgetConfig)));

    this.displayDialog = true;
  }

  dialogSaved(formValue) {

    const configurator = this.widgetAdminHandlerProvider.get(this.selectedWidgetConfiguration.widgetTypeId);
    const name = formValue.name;
    const copyOfSelectedConfig: WidgetConfiguration = lodash.cloneDeep(this.selectedWidgetConfiguration);
    configurator.saveConfigFromAdminForm(copyOfSelectedConfig, formValue).pipe(
      take(1),
      tap(() => {
        this.store.dispatch(new widgetConfigActions.LoadAll(this.product));
        this.messageService.success(`Widget config ${name} saved`, 'Save Successful');
        this.handleInitialisation(configurator).pipe(take(1)).subscribe();
        this.closeDialog();
      }),
      catchError((err) => {
        const msg = 'An error occured during save.';
        this.messageService.error(`Widget config ${name} not saved${(msg ? msg : '')}`.trim(), 'Save Error');
        return empty();
      }),
    ).subscribe();

  }

  /**
   * formValue is the FormGroup.value see dynamic form component. typed as any...
   * @param formValue
   */
  createCopy(formValue) {

    const configurator = this.widgetAdminHandlerProvider.get(this.selectedWidgetConfiguration.widgetTypeId);
    const name = formValue.name;
    const copyOfSelectedConfig: WidgetConfiguration = lodash.cloneDeep(this.selectedWidgetConfiguration);
    configurator.createCopyFromAdminForm(copyOfSelectedConfig, formValue).pipe(
      take(1),
      tap(() => {
        this.store.dispatch(new widgetConfigActions.LoadAll(this.product));
        this.messageService.success(`Widget config ${name} saved`, 'Create Copy Successful');
        this.closeDialog();
        this.handleInitialisation(configurator).pipe(take(1)).subscribe();
      }),
      catchError((err) => {
        const msg = err.error.Message ? `: ${err.error.Message}` : undefined;
        this.messageService.error(`Widget config ${name} not saved${(msg ? msg : '')}`.trim(), 'Create Copy Error');
        return empty();
      })
    ).subscribe();
  }

  delete() {
    this.displayDeleteDialog = true;
  }

  performDelete(event) {

    const formValue = event;
    const configurator = this.widgetAdminHandlerProvider.get(this.selectedWidgetConfiguration.widgetTypeId);
    const name = formValue.name;
    const copyOfSelectedConfig: WidgetConfiguration = lodash.cloneDeep(this.selectedWidgetConfiguration);
    configurator.deleteConfigFromAdminForm(copyOfSelectedConfig, formValue).pipe(
      take(1),
      map(() => {
        this.store.dispatch(new widgetConfigActions.LoadAll(this.product));
        this.messageService.success(`Widget config ${name} Deleted`, 'Delete Successful');
        this.closeDialog();
        return true;
      }),
      catchError((err) => {
        const msg = err.error.Message ? `: ${err.error.Message}` : undefined;
        this.messageService.error(`Error Deleting Widget config ${name}: ${msg}`, 'Delete Error');
        return empty();
      })
    ).subscribe();

  }
  resetAllUsersToDefaultColumns() {
    const name = this.selectedWidgetConfiguration.name;
    this.widgetColumnsHttpService.deleteUserSpecifiedWidgetColumns(this.selectedWidgetConfiguration.id).pipe(
      take(1),
      tap(() => {
        this.store.dispatch(new widgetConfigActions.LoadAll(this.product));
        this.messageService.success(`Resetting the columns to default for ${name}`, 'Reset Successful');
      }),
      catchError((err) => {
        const msg = `An error occured resitting the ${name} columns back to the default.`;
        this.messageService.error(`Columns not reset for ${name} ${(msg ? msg : '')}`.trim(), 'Column reset Error');
        return empty();
      }),
    ).subscribe();
  }

  setPageTitle(): void {
    const product = this.routeContextService.getProductFromRouteData(this.activatedRoute);
    const title = product === MojoWellKnownNames.Products.MojoDashboard ? 'Manage Widgets' : 'Manage My Widgets';
    this.pageTitle$ = this.productService.currentProductTitle(title, product);
  }

  closeDialog() {
    this.displayDialog = false;
    this.displayDeleteDialog = false;
    this.formDefinition = undefined;
  }
}
